/**
 * The words one catalog item is called by — `docs/visit-page.md` §8, §9
 * (Boards 2 and 4), migration 0155.
 *
 * An item's `name` is the words its line carries — *Wall caps, Belgard
 * Bullnose · Pewter* — because a quote line, a plan line and a visit's
 * material all copy it, and a swapped line has to read as its new item.
 * `short_name` is the crew's noun (*caps*). The swap sheet lists catalog items
 * in the catalog's own words — *Belgard Bullnose caps, Pewter* — and a
 * sentence names the model (*Mini-Creta backordered*); both are made here from
 * `items.attributes`, the facts §9.2's *Best match* compares anyway, so no
 * second name is typed into a column to drift from the first.
 */
import { z } from 'zod';
const attribute = z.string().trim().min(1).max(80);
/**
 * `items.attributes` — every key optional, nothing else allowed. A writer
 * parses through this, and the database's `items_attributes_shape` (0155)
 * refuses the same things, so a typo is refused rather than stored where
 * *Best match* would never read it.
 */
export const itemAttributesSchema = z.object({
    /** *Belgard*, *Techo-Bloc*. */
    brand: attribute.optional(),
    /** *Bullnose*, *Mini-Creta* — the word a sentence uses for this one. */
    model: attribute.optional(),
    /** *Pewter*, *Shale Grey*. */
    colour: attribute.optional(),
    /** *Tumbled*, *Smooth*. */
    finish: attribute.optional(),
    /** *46 in*, *12.5 × 100 ft*. */
    size: attribute.optional(),
}).strict();
/** The crew's noun — *caps*, *kit* — else the name. */
export function itemNoun(item) {
    return item.shortName ?? item.name;
}
/**
 * The catalog's own words — *Belgard Bullnose caps, Pewter* — brand, model
 * and noun, then the colour (and finish) after a comma. The name itself
 * when the attributes do not say a model.
 */
export function catalogName(item) {
    const { brand, model, colour, finish } = item.attributes;
    if (!model)
        return item.name;
    const head = [brand, model, item.shortName].filter(Boolean).join(' ');
    const tail = [colour, finish].filter(Boolean).join(', ');
    return tail ? `${head}, ${tail}` : head;
}
/** The model a sentence names — *Bullnose*, *Mini-Creta* — else the crew's noun. */
export function modelNoun(item) {
    return item.attributes.model ?? itemNoun(item);
}
//# sourceMappingURL=item-words.js.map